/**
 * JourneyShell — focused-entry navigator over the misty background.
 *
 * Shows one entry at a time as a translucent "paper" card centered over the mist.
 * Advancing slides the next entry in from the right; going back slides from the
 * left. A slim progress indicator + Back/Next controls sit below. The entry's
 * inner content is rendered by its registered module (lookup by entry.type).
 *
 * Receives the useJourney() shape via props so the page owns data, shell owns UX.
 */

import { useEffect, useState, useRef } from 'react'
import { getModule } from './modules/index.js'
import JourneyAnticipation from './JourneyAnticipation.jsx'
import SigningModal from './SigningModal.jsx'
import { fmtJourneyDate } from './journeyUI.jsx'
import { subscribeToJourney } from '../services/journey.js'
import { entryPhase, entryDateISO } from './journeyOrder.js'

// Display date for an entry (post date when inferred, else the manual journey-entry date).
function entryDate(e) { return fmtJourneyDate(entryDateISO(e)) }

// Roadmap title — must match what the card shows: an explicit title, else the linked milestone's
// real name, else the module label.
function entryTitle(e, mod) { return e.title || (e.milestone && e.milestone.name) || (mod && mod.label) || 'Entry' }

// Roadmap search: an entry matches when every typed token appears in its title + type label + phase.
function entryMatches(e, phase, tokens) {
  const mod = getModule(e.type)
  const hay = [entryTitle(e, mod), (mod && mod.label) || e.type, phase].join(' ').toLowerCase()
  return tokens.every(t => hay.includes(t))
}

// Group entries under phase branches. Takes parent-indexed pairs ({ e, i }) so each item keeps its
// real carousel index. Branch order follows the project's real phase order (phaseOrder, kept in sync
// from the Phases table); any phase present but not in that list is appended in first-appearance order.
function groupByPhase(pairs, phaseOrder) {
  const byPhase = {}
  const seen = []
  pairs.forEach(({ e, i }) => {
    const ph = entryPhase(e)
    if (!byPhase[ph]) { byPhase[ph] = []; seen.push(ph) }
    byPhase[ph].push({ e, i })
  })
  const ordered = []
  ;(phaseOrder || []).forEach(p => { if (byPhase[p] && !ordered.includes(p)) ordered.push(p) })
  seen.forEach(p => { if (!ordered.includes(p)) ordered.push(p) })
  return ordered.map(ph => ({ phase: ph, items: byPhase[ph] }))
}

// Agreements (money / contract docs) live on their own surface, not the design Roadmap. The FIRST
// proposal is the conversion gateway and stays in the roadmap's opening beat; ASRs and any later
// proposals are "agreements". `firstProposalIdx` is the lowest-positioned proposal entry.
const AGREEMENT_LABEL = { asr_review: 'Additional Services Request', proposal: 'Proposal' }

// Package labels mirrored from ProposalReviewEntry — must stay in sync.
const PACKAGE_LABEL_MAP = { A: 'Essential Design', B: 'Premier Architecture', C: 'Architecture + Interiors' }

// Build the SigningModal props from an agreement entry's data fields.
function buildSigningParams(e) {
  const d = e.data || {}
  if (e.type === 'asr_review') {
    return {
      pdfUrl: d.pdfUrl || null,
      title: d.title || `Additional Services Request${d.number ? ' No. ' + d.number : ''}`,
      subtitle: 'Additional Services Request',
      anchorQueries: ['Accepted by (signature)', 'Accepted by'],
      consentLabel: 'I agree to sign this additional services request electronically, and that my electronic signature is the legal equivalent of my handwritten signature and constitutes my acceptance of these terms.',
      packageOptions: null,
    }
  }
  // proposal
  const tiered = !!d.tiered && d.packages && (d.packages.A || d.packages.B || d.packages.C)
  const packageOptions = tiered
    ? ['A', 'B', 'C'].filter(k => d.packages[k]).map(k => ({ key: k, label: PACKAGE_LABEL_MAP[k], fee: d.packages[k] }))
    : null
  return {
    pdfUrl: d.pdfUrl || null,
    title: d.proposalType || 'Proposal for Architectural Services',
    subtitle: 'Proposal',
    anchorQueries: ['Client -', 'Accepted by', 'ACCEPTANCE'],
    consentLabel: 'I have reviewed this proposal and agree to its terms. I agree to sign electronically, and that my electronic signature is the legal equivalent of my handwritten signature.',
    packageOptions,
  }
}
function makeIsAgreement(entries) {
  const firstProposalIdx = (entries || []).findIndex(e => e.type === 'proposal')
  return (e, i) => e?.type === 'asr_review' || (e?.type === 'proposal' && i !== firstProposalIdx)
}

// A card's scrolling body. When there is more below (Matt, 2026-11-02: "otherwise it's not
// immediately clear"), a soft fade and a small down arrow sit at the bottom of the card; they go
// away once the reader reaches the end. The arrow scrolls down most of a card's height. Content can
// grow after it renders (books, images, a form opening), so the inner size is watched, not just
// measured once.
function CardScroll({ active, children }) {
  const scrollRef = useRef(null)
  const innerRef = useRef(null)
  const [more, setMore] = useState(false)
  useEffect(() => {
    const el = scrollRef.current
    if (!el || !active) { setMore(false); return }
    const check = () => setMore(el.scrollHeight - el.scrollTop - el.clientHeight > 12)
    check()
    el.addEventListener('scroll', check, { passive: true })
    let ro = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(check)
      ro.observe(el)
      if (innerRef.current) ro.observe(innerRef.current)
    }
    return () => { el.removeEventListener('scroll', check); if (ro) ro.disconnect() }
  }, [active])
  const down = () => { const el = scrollRef.current; if (el) el.scrollBy({ top: el.clientHeight * 0.7, behavior: 'smooth' }) }
  return (
    <>
      <div ref={scrollRef} className="aj-jrn-scroll" style={{ ...S.cardScroll, overflowY: active ? 'auto' : 'hidden' }}>
        <div ref={innerRef}>{children}</div>
      </div>
      <div aria-hidden={!more} style={{ ...S.moreFade, opacity: more ? 1 : 0 }}>
        <button type="button" onClick={down} tabIndex={more ? 0 : -1} aria-label="Scroll down for more" style={{ ...S.moreBtn, pointerEvents: more ? 'auto' : 'none' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="aj-jrn-more-arrow"><path d="M6 9l6 6 6-6" /></svg>
        </button>
      </div>
    </>
  )
}

export default function JourneyShell({ project, entries, phases, index, total, responseFor, respond, goNext, goPrev, goTo, token, previewMode, billingOpenRef, billingSummary, subscribedEmail }) {
  const [navOpen, setNavOpen] = useState(false)
  const [agreementsModalOpen, setAgreementsModalOpen] = useState(false)
  const [asrPopup, setAsrPopup] = useState(null)
  const [signingTarget, setSigningTarget] = useState(null)
  const [signingBusy, setSigningBusy] = useState(false)
  const [collapsedPhases, setCollapsedPhases] = useState(() => new Set())
  const [query, setQuery] = useState('')
  const [winW, setWinW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280)
  const [winH, setWinH] = useState(typeof window !== 'undefined' ? window.innerHeight : 800)
  const focusedOnce = useRef(false)
  const activeDrawerRef = useRef(null)

  const isAgreement = makeIsAgreement(entries)

  function togglePhase(ph) {
    setCollapsedPhases(prev => {
      const next = new Set(prev)
      if (next.has(ph)) next.delete(ph); else next.add(ph)
      return next
    })
  }

  useEffect(() => {
    const onResize = () => { setWinW(window.innerWidth); setWinH(window.innerHeight) }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Open the roadmap focused on where the client is: once entries load, collapse every phase except
  // the one holding the focused entry (which stays open, with that entry highlighted). Runs once.
  useEffect(() => {
    if (focusedOnce.current || !entries || !entries.length) return
    focusedOnce.current = true
    const currentPhase = entries[index] ? entryPhase(entries[index]) : null
    const rm = entries.map((e, i) => ({ e, i })).filter(({ e, i }) => !isAgreement(e, i))
    const s = new Set()
    groupByPhase(rm, phases).forEach(g => { if (g.phase !== currentPhase) s.add(g.phase) })
    setCollapsedPhases(s)
  }, [entries, phases, index])

  // ASR new-item popup — shown once (per token+entry, persisted in localStorage) when an unsigned ASR exists.
  useEffect(() => {
    if (!entries?.length || !token) return
    const firstProposalIdx = entries.findIndex(e => e.type === 'proposal')
    const isAgr = (e, i) => e?.type === 'asr_review' || (e?.type === 'proposal' && i !== firstProposalIdx)
    const idx = entries.findIndex((e, i) => isAgr(e, i) && e.type === 'asr_review' && !responseFor(e.id))
    if (idx < 0) return
    const e = entries[idx]
    try { if (localStorage.getItem(`asr-seen:${token}:${e.id}`)) return } catch {}
    setAsrPopup({ e, i: idx })
  }, [entries]) // eslint-disable-line react-hooks/exhaustive-deps

  // The Getting started tour (HubTour.jsx) opens and closes the Roadmap to show what is inside it.
  useEffect(() => {
    const onTour = ev => { const d = ev.detail || {}; if (typeof d.open === 'boolean') setNavOpen(d.open) }
    window.addEventListener('hub-tour-roadmap', onTour)
    return () => window.removeEventListener('hub-tour-roadmap', onTour)
  }, [])

  // Reset the search when the roadmap closes so it reopens clean.
  useEffect(() => { if (!navOpen) setQuery('') }, [navOpen])

  // Scroll the active drawer item into view whenever the drawer opens.
  useEffect(() => {
    if (!navOpen) return
    const t = setTimeout(() => {
      activeDrawerRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }, 120)
    return () => clearTimeout(t)
  }, [navOpen])

  // Carousel geometry: on wider viewports the neighbors peek in at the edges; on narrow
  // viewports we show the focused card only (neighbors fade fully out).
  const peek = winW > 760
  const CARD_W = peek ? Math.min(600, winW - 280) : Math.min(640, winW - 40)
  // Fixed card height: all cards are the same height regardless of content — content scrolls inside.
  // Reserve ~160px for header + footer; never shorter than 320px (usable on very short/landscape mobile).
  const CARD_H = Math.max(Math.min(peek ? 680 : 600, winH - 160), 320)
  const GAP = 26
  const STEP = CARD_W + GAP

  // No entries yet → the mysterious anticipation screen (handed to a prospect right after conversion
  // to build suspense). Replaces the whole shell until the first entry (e.g. the proposal) lands.
  // EXCEPT: a project whose proposal was accepted outside the app (the old-fashioned way, before
  // this journey existed, or through a channel other than the in-app CRM flow) never gets a
  // journey_entries row — entries.length would stay 0 forever even though there's real content to
  // show. project.signedAgreements (fetched from Airtable, same source the Agreements modal below
  // already displays) is the signal that this is that case rather than a genuinely fresh,
  // nothing-posted-yet project — skip the suspense screen and fall through to the normal shell,
  // which already renders an "Agreements & Signatures" entry point for exactly this data.
  const hasLegacyAgreements = (project?.signedAgreements || []).length > 0
  if ((!entries || entries.length === 0) && !hasLegacyAgreements) {
    return <JourneyAnticipation project={project} previewMode={previewMode} token={token} />
  }

  const entry = entries[index]
  const roadmap = entries.map((e, i) => ({ e, i })).filter(({ e, i }) => !isAgreement(e, i))
  const agreements = entries
    .map((e, i) => ({ e, i }))
    .filter(({ e, i }) => isAgreement(e, i))
    .sort((a, b) => {
      // ASRs sort after proposals
      const aAsr = a.e.type === 'asr_review', bAsr = b.e.type === 'asr_review'
      if (aAsr !== bAsr) return aAsr ? 1 : -1
      if (aAsr && bAsr) return ((a.e.data || {}).number || 0) - ((b.e.data || {}).number || 0)
      // Among proposals: DC before BS
      const aIsDc = String((a.e.data || {}).proposalType || '').includes('Design Consultation')
      const bIsDc = String((b.e.data || {}).proposalType || '').includes('Design Consultation')
      if (aIsDc !== bIsDc) return aIsDc ? -1 : 1
      return 0
    })

  // Pre-signed agreements fetched from Airtable (accepted before the journey system, or accepted
  // via a channel other than the journey). Deduplicated against journey-entry proposals by record ID.
  const journeyProposalIds = new Set(
    agreements.filter(({ e }) => e.type === 'proposal').map(({ e }) => e.data?.proposalId).filter(Boolean)
  )
  const preSignedAgreements = (project?.signedAgreements || [])
    .filter(sa => !journeyProposalIds.has(sa.id))
    .sort((a, b) => {
      if (a.type === 'Design Consultation' && b.type !== 'Design Consultation') return -1
      if (b.type === 'Design Consultation' && a.type !== 'Design Consultation') return 1
      return 0
    })

  // Carousel is always the roadmap — agreements never appear as carousel cards.
  const lanePos = Math.max(0, roadmap.findIndex(x => x.i === index))
  const laneTotal = roadmap.length
  const unsignedAgreements = agreements.filter(({ e }) => !responseFor(e.id)).length

  // Combined notification dot on the Roadmap button covers unsigned agreements, unpaid invoices,
  // and (2026-07) any roadmap entry published since this recipient's last visit (e.isNew — see
  // api/journey.js, only ever set for a per-recipient token with a prior visit on record).
  const hasNewEntries = roadmap.some(({ e }) => e.isNew)
  const combinedDot = unsignedAgreements > 0 || !!(billingSummary?.hasUnpaid) || !!(billingSummary?.ratesUpdated) || hasNewEntries

  function go(parentIdx) { if (goTo && parentIdx != null) goTo(parentIdx) }
  function jumpTo(i) { go(i); setNavOpen(false); setAgreementsModalOpen(false) }
  function laneNext() { if (lanePos < roadmap.length - 1) go(roadmap[lanePos + 1].i) }
  function lanePrev() { if (lanePos > 0) go(roadmap[lanePos - 1].i) }

  // Arrow keys move within the roadmap (ignored while the drawer is open).
  useEffect(() => {
    if (navOpen) return
    const onKey = e => {
      if (e.key === 'ArrowRight') laneNext()
      else if (e.key === 'ArrowLeft') lanePrev()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, navOpen, lanePos, laneTotal]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={S.root}>
      <style>{KEYFRAMES}</style>

      {/* Roadmap toggle (top-left) — combined dot when agreements or invoices need attention */}
      {/* The wrapper holds fixed position so the dot (a sibling of the button) is never subject to
          the button's faded opacity — CSS opacity on a parent clamps all descendants with no escape. */}
      {total > 0 && (
        <div data-tour="roadmap" style={{ position: 'fixed', top: 34, left: 24, zIndex: 3 }}>
          <button onClick={() => setNavOpen(true)} aria-label="Show roadmap"
            style={{ ...S.contentsBtn, position: 'relative', top: 'auto', left: 'auto', zIndex: 'auto', opacity: navOpen ? 0 : 0.55, pointerEvents: navOpen ? 'none' : 'auto' }}
            onMouseEnter={e => { if (!navOpen) e.currentTarget.style.opacity = 1 }}
            onMouseLeave={e => { if (!navOpen) e.currentTarget.style.opacity = 0.55 }}>
            <span style={{ display: 'inline-flex' }}>
              <span className="aj-jrn-lines"><i /><i /><i /></span>
            </span>
            <span style={S.contentsLabel}>Roadmap</span>
          </button>
          {combinedDot && <span style={S.combinedDot} />}
        </div>
      )}

      {/* Scrim */}
      <div onClick={() => setNavOpen(false)}
        style={{ ...S.scrim, opacity: navOpen ? 1 : 0, pointerEvents: navOpen ? 'auto' : 'none' }} />

      {/* Left drawer — Roadmap navigation */}
      <aside data-tour="roadmap-drawer" style={{ ...S.drawer, transform: navOpen ? 'translateX(0)' : 'translateX(-100%)' }}>
        <div style={S.drawerHead}>
          <div style={S.drawerEyebrow}>Roadmap</div>
          {project?.name && <div style={S.drawerProject}>{project.name}</div>}
          {/* On a phone the Getting started checklist lives here (HubTour.jsx portals into it). */}
          <div id="hub-tour-drawer-slot" />
          <div style={{ position: 'relative', marginTop: 12 }}>
            <input
              type="text" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search the roadmap…" style={S.drawerSearch}
            />
            {query && (
              <button onClick={() => setQuery('')} title="Clear" style={S.drawerSearchClear}>×</button>
            )}
          </div>
        </div>
        <nav data-tour="roadmap-list" style={S.drawerList}>
          {(() => {
          const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
          const groups = groupByPhase(roadmap, phases)
            .map(({ phase, items }) => ({ phase, items: tokens.length ? items.filter(({ e }) => entryMatches(e, phase, tokens)) : items }))
            .filter(({ items }) => !tokens.length || items.length)
          if (tokens.length && !groups.length) {
            return <div style={S.drawerEmpty}>No entries match "{query.trim()}".</div>
          }
          return groups.map(({ phase, items }) => {
            const collapsed = tokens.length ? false : collapsedPhases.has(phase)
            const hasCurrent = items.some(({ i }) => i === index)
            return (
              <div key={phase} style={{ marginBottom: 6 }}>
                <button onClick={() => togglePhase(phase)} style={S.phaseHdr}
                  onMouseEnter={ev => { ev.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
                  onMouseLeave={ev => { ev.currentTarget.style.background = 'transparent' }}>
                  <span style={{ ...S.phaseTri, transform: collapsed ? 'rotate(-90deg)' : 'none' }}>▾</span>
                  <span style={{ ...S.phaseName, color: hasCurrent ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.66)' }}>{phase}</span>
                  <span style={S.phaseCount}>{items.length}</span>
                </button>
                {!collapsed && items.map(({ e, i }) => {
                  const mod = getModule(e.type)
                  const responded = !!responseFor(e.id)
                  const isCurrent = i === index
                  const date = entryDate(e)
                  return (
                    <button key={e.id}
                      ref={isCurrent ? el => { activeDrawerRef.current = el } : undefined}
                      onClick={() => jumpTo(i)}
                      style={{ ...S.drawerItem, paddingLeft: 22, ...(isCurrent ? S.drawerItemActive : null) }}
                      onMouseEnter={ev => { if (!isCurrent) ev.currentTarget.style.background = 'rgba(255,255,255,0.06)' }}
                      onMouseLeave={ev => { if (!isCurrent) ev.currentTarget.style.background = 'transparent' }}>
                      <span style={S.drawerNum}>{String(i + 1).padStart(2, '0')}</span>
                      <span style={S.drawerItemBody}>
                        <span style={S.drawerItemTitle}>{entryTitle(e, mod)}</span>
                        <span style={S.drawerItemType}>{mod?.icon ? mod.icon + '  ' : ''}{mod?.label || e.type}</span>
                        {date && <span style={S.drawerItemDate}>{date}</span>}
                      </span>
                      {previewMode && !e.published && <span style={S.drawerDraft}>Draft</span>}
                      {e.isNew && !responded && <span style={S.drawerNewBadge}>New</span>}
                      {responded && <span style={S.drawerDot} title="Completed">✓</span>}
                    </button>
                  )
                })}
              </div>
            )
          })
          })()}
        </nav>
        {/* Agreements & Invoices shortcuts — pinned above the email subscribe box */}
        {(agreements.length > 0 || preSignedAgreements.length > 0 || billingSummary?.hasInvoices || billingSummary?.hasRates) && (
          <div data-tour="shortcuts" style={S.drawerShortcuts}>
            {(agreements.length > 0 || preSignedAgreements.length > 0) && (
              <button
                onClick={() => { setNavOpen(false); setAgreementsModalOpen(true) }}
                style={S.drawerShortcutBtn}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}>
                <span style={S.drawerShortcutLabel}>Agreements</span>
                {unsignedAgreements > 0 && <span style={S.agreementsDot} title={`${unsignedAgreements} awaiting signature`} />}
              </button>
            )}
            {billingSummary?.hasInvoices && (
              <button data-tour="invoices"
                onClick={() => { setNavOpen(false); if (billingOpenRef?.current) billingOpenRef.current() }}
                style={S.drawerShortcutBtn}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}>
                <span style={S.drawerShortcutLabel}>Invoices</span>
                {billingSummary?.hasUnpaid && <span style={S.agreementsDot} />}
              </button>
            )}
            {/* Current hourly rates (2026-11-03) — from signing on; a dot when a new year's rates arrive. */}
            {billingSummary?.hasRates && (
              <button
                onClick={() => { setNavOpen(false); if (billingOpenRef?.current) billingOpenRef.current('rates') }}
                style={S.drawerShortcutBtn}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}>
                <span style={S.drawerShortcutLabel}>Rates</span>
                {billingSummary?.ratesUpdated && <span style={S.agreementsDot} title="Updated rates" />}
              </button>
            )}
          </div>
        )}
        {token && !previewMode && <SubscribeBox token={token} subscribedEmail={subscribedEmail} />}
      </aside>

      {/* Top: brand + project */}
      <header style={S.header}>
        <div style={S.wordmark}>ALDER JAMES</div>
        {project?.name && <div style={S.projectName}>{project.name}</div>}
      </header>

      {/* Focused entry card — a peek carousel: the active card is centered, neighbors slide in
          from the sides and fade at the edges. Arrows / keys shift the track. */}
      <main style={{ ...S.stage, ...(peek ? S.stageMask : null) }}>
        {!entry ? (
          // Content-sized card (not the full carousel height) — a fixed CARD_H slab around three
          // lines of text was the "huge blank white void" complaint. A legacy project with a
          // proposal accepted outside the journey (see JourneyShell's entries-empty gate) lands
          // here too, so it gets a direct way to its agreement rather than only the drawer.
          <div style={{ ...S.card, width: CARD_W, maxWidth: '100%', minHeight: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'clamp(36px, 6vw, 56px) clamp(28px, 5vw, 48px)' }}>
            <div style={S.emptyMark}><span style={S.emptyMarkDot} /></div>
            <div style={S.emptyEyebrow}>{project?.name || ''}</div>
            <div style={S.emptyTitle}>Being prepared</div>
            <p style={{ ...S.emptyBody, maxWidth: 340, textAlign: 'center' }}>Your team is putting this together. Please check back shortly.</p>
            {(agreements.length > 0 || preSignedAgreements.length > 0) && (
              <button onClick={() => setAgreementsModalOpen(true)} style={S.emptyCta}>View your proposal</button>
            )}
          </div>
        ) : (
          <div style={S.track}>
            {/* A hairline in the gap wherever the next card starts a new phase, named for that phase.
                Shown only beside the focused card (the others are faded out anyway). */}
            {peek && roadmap.map(({ e }, j) => {
              if (j === 0) return null
              const ph = entryPhase(e)
              if (ph === entryPhase(roadmap[j - 1].e)) return null
              const dist = j - lanePos
              const sL = dist - 1 === 0 ? 1 : 0.86, sR = dist === 0 ? 1 : 0.86
              const x = ((dist - 1) * STEP + sL * CARD_W / 2 + dist * STEP - sR * CARD_W / 2) / 2
              const shown = dist === 0 || dist === 1
              return (
                <div key={'phase-div:' + e.id} aria-hidden="true"
                  style={{ ...S.phaseDivider, height: CARD_H * 0.62, marginTop: -CARD_H * 0.31, transform: `translateX(${x}px)`, opacity: shown ? 1 : 0 }}>
                  <span style={S.phaseDividerLine} />
                  <span style={S.phaseDividerLabel}>{ph}</span>
                  <span style={S.phaseDividerLine} />
                </div>
              )
            })}
            {roadmap.map(({ e, i }, j) => {
              const dist = j - lanePos
              const isActive = j === lanePos
              const mod = getModule(e.type)
              const Comp = mod?.component
              const date = entryDate(e)
              // Beyond the immediate neighbors, fully fade out (and stop rendering the heavy module
              // content) so the carousel stays light and the edges read as "coming into view".
              const faded = Math.abs(dist) > (peek ? 1 : 0)
              return (
                <div key={e.id}
                  onClick={!isActive ? () => jumpTo(i) : undefined}
                  style={{
                    ...S.slot,
                    width: CARD_W,
                    marginLeft: -CARD_W / 2,
                    transform: `translateX(${dist * STEP}px) scale(${isActive ? 1 : 0.86})`,
                    opacity: faded ? 0 : (isActive ? 1 : 0.5),
                    pointerEvents: faded ? 'none' : 'auto',
                    cursor: isActive ? 'default' : 'pointer',
                    zIndex: isActive ? 2 : 1,
                  }}
                >
                  <div data-tour={isActive ? 'entry-card' : undefined} style={{ ...S.card, width: '100%', height: CARD_H, display: 'flex', flexDirection: 'column', pointerEvents: isActive ? 'auto' : 'none' }}>
                    {date && <div style={S.cardDate}>{date}</div>}
                    <CardScroll active={isActive}>
                      {previewMode && !e.published && <div style={S.draftPill}>Draft — not visible to client</div>}
                      {e.isNew && !previewMode && <div style={S.newPill}>New since your last visit</div>}
                      {Comp && (Math.abs(dist) <= 1)
                        ? <Comp
                            entry={e}
                            project={project}
                            response={responseFor(e.id)}
                            onRespond={(kind, payload) => respond(e.id, kind, payload)}
                            isActive={isActive}
                            token={token}
                            previewMode={previewMode}
                            viewOnly={project?.viewerRole === 'owners_rep'}
                          />
                        : Comp ? null : <div style={S.emptyBody}>Unknown entry type: {e.type}</div>}
                    </CardScroll>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </main>

      {/* Bottom: progress + Back/Next controls */}
      {laneTotal > 0 && (
        <footer data-tour="entry-nav" style={S.footer}>
          <button onClick={lanePrev} disabled={lanePos === 0} style={{ ...S.nav, opacity: lanePos === 0 ? 0.3 : 1 }}>
            ← Back
          </button>
          <div style={S.progress}>
            {String(lanePos + 1).padStart(2, '0')} <span style={{ opacity: 0.4 }}>/ {String(laneTotal).padStart(2, '0')}</span>
          </div>
          <button onClick={laneNext} disabled={lanePos >= laneTotal - 1} style={{ ...S.nav, opacity: lanePos >= laneTotal - 1 ? 0.3 : 1 }}>
            Next →
          </button>
        </footer>
      )}

      {/* Agreements modal — centered overlay (like invoices) for all agreements and signatures */}
      {agreementsModalOpen && (
        <div style={S.agModalOverlay} onClick={e => { if (e.target === e.currentTarget) setAgreementsModalOpen(false) }}>
          <div style={S.agModal}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={S.drawerEyebrow2}>Agreements &amp; Signatures</div>
              <button onClick={() => setAgreementsModalOpen(false)} style={S.agModalClose}>×</button>
            </div>
            {agreements.length === 0 && preSignedAgreements.length === 0 && (
              <div style={S.agModalEmpty}>Nothing to sign right now.</div>
            )}
            {/* Pre-signed agreements (accepted outside the journey) */}
            {preSignedAgreements.map((sa, sai) => {
              const title = sa.type === 'Design Consultation'
                ? 'Design Consultation Agreement'
                : 'Agreement for Architectural Services'
              const sub = [sa.type, sa.signedAt ? fmtJourneyDate(sa.signedAt) : null].filter(Boolean).join('  ·  ')
              return (
                <div key={sa.id} style={{ ...S.agModalItem, cursor: 'default', ...(sai === 0 ? { borderTop: 'none' } : null) }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.agModalTitle}>{title}</div>
                    <div style={S.agModalSub}>{sub}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{ ...S.agStatus, ...S.agStatusSigned }}>Signed</span>
                    {sa.pdfUrl && (
                      <a href={sa.pdfUrl} target="_blank" rel="noopener noreferrer" title="Open agreement"
                         style={{ fontSize: 15, textDecoration: 'none', lineHeight: 1, color: '#6b8f95' }}>&#128196;</a>
                    )}
                  </div>
                </div>
              )
            })}
            {/* Journey agreements (ASRs + secondary proposals) — the row itself is inert (agreements
                have no carousel card to jump to; a whole-row click here used to call jumpTo(i), which
                landed nowhere real). The document icon is the ONLY actionable element: unsigned ->
                opens the signing modal; signed -> opens the executed PDF (Certificate of Completion +
                audit trail baked in via api/asr-pdf.js / api/proposal-pdf.js), never the draft/posted
                copy still sitting in entry.data.pdfUrl. */}
            {agreements.map(({ e, i }, ai) => {
              const mod = getModule(e.type)
              const response = responseFor(e.id)
              const signed = !!response
              const d = e.data || {}
              const isAsr = e.type === 'asr_review'
              const hasPdf = !!d.pdfUrl
              // The executed copy lives on the response payload (written by journey-respond.js after
              // signing), not on entry.data — that field is frozen at post-time and never updated.
              const signedPdfUrl = signed ? (response.payload && response.payload.signedPdfUrl) : null
              const openUrl = signedPdfUrl || d.pdfUrl
              // Proposals have a second, internal countersignature step (api/proposal-countersign.js)
              // after the client signs — ASRs execute fully at client-sign, no such step exists for them.
              const isProposal = e.type === 'proposal'
              const countersigned = isProposal ? !!(response?.payload?.countersignedAt) : true
              const sub = [
                AGREEMENT_LABEL[e.type] || (mod?.label || e.type),
                isAsr && d.number ? `No. ${d.number}` : null,
                isAsr && d.issueDate ? fmtJourneyDate(d.issueDate) : (!isAsr ? entryDate(e) : null),
              ].filter(Boolean).join('  ·  ')
              function handleIconClick(ev) {
                ev.stopPropagation()
                if (!signed && hasPdf) {
                  setAgreementsModalOpen(false)
                  setSigningTarget({ entry: e })
                } else if (openUrl) {
                  window.open(openUrl, '_blank')
                }
              }
              return (
                <div key={e.id}
                  style={{ ...S.agModalItem, cursor: 'default', ...(ai === 0 && preSignedAgreements.length === 0 ? { borderTop: 'none' } : null) }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.agModalTitle}>{entryTitle(e, mod)}</div>
                    <div style={S.agModalSub}>{sub}</div>
                  </div>
                  <span style={{ ...S.agStatus, ...(signed && countersigned ? S.agStatusSigned : S.agStatusPending) }}>
                    {signed ? (countersigned ? 'Signed' : 'Pending countersignature') : 'Review & sign'}
                  </span>
                  {(hasPdf || openUrl) && (
                    <span
                      title={signed ? 'Open executed agreement' : 'Review & sign'}
                      onClick={handleIconClick}
                      style={{ fontSize: 15, lineHeight: 1, color: '#6b8f95', cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}>
                      &#128196;
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Signing modal — opened directly from the Agreements modal or ASR popup (bypasses the
          carousel card, same pattern as invoices' "View & pay"). zIndex 60 sits above everything. */}
      {signingTarget && (() => {
        const params = buildSigningParams(signingTarget.entry)
        if (!params.pdfUrl) return null
        return (
          <SigningModal
            pdfUrl={params.pdfUrl}
            title={params.title}
            subtitle={params.subtitle}
            anchorQueries={params.anchorQueries}
            packageOptions={params.packageOptions}
            consentLabel={params.consentLabel}
            busy={signingBusy}
            onClose={() => setSigningTarget(null)}
            onSubmit={async payload => {
              if (signingBusy) return
              setSigningBusy(true)
              try { await respond(signingTarget.entry.id, 'approve', payload); setSigningTarget(null) }
              finally { setSigningBusy(false) }
            }}
          />
        )
      })()}

      {/* ASR notification popup — shown once (per localStorage key) when an unsigned ASR exists */}
      {asrPopup && (
        <div style={{ ...S.agModalOverlay, zIndex: 55 }}>
          <div style={{ ...S.agModal, maxWidth: 420 }}>
            <div style={{ fontFamily: '"Montserrat", sans-serif', fontSize: 9, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#7a9aaa', marginBottom: 10 }}>
              Additional Services Request{asrPopup.e.data?.number ? ` · No. ${asrPopup.e.data.number}` : ''}
            </div>
            <div style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 300, fontSize: 20, letterSpacing: '0.04em', color: '#07272D', lineHeight: 1.3 }}>
              {entryTitle(asrPopup.e, getModule(asrPopup.e.type))}
            </div>
            <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 14, color: '#3a5a60', marginTop: 10, lineHeight: 1.6 }}>
              A new additional services request has been issued for your review and signature.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end', alignItems: 'center' }}>
              <button onClick={() => {
                try { localStorage.setItem(`asr-seen:${token}:${asrPopup.e.id}`, '1') } catch {}
                setAsrPopup(null)
              }}
                style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#5a7884', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 4px' }}>
                Dismiss
              </button>
              <button onClick={() => {
                try { localStorage.setItem(`asr-seen:${token}:${asrPopup.e.id}`, '1') } catch {}
                setAsrPopup(null)
                if ((asrPopup.e.data || {}).pdfUrl) {
                  setSigningTarget({ entry: asrPopup.e })
                } else {
                  jumpTo(asrPopup.i)
                }
              }}
                style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 500, color: '#fff', background: '#07272D', border: 'none', borderRadius: 6, padding: '8px 18px', cursor: 'pointer' }}>
                Review &amp; sign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Email-updates opt-in, pinned at the bottom of the Roadmap drawer.
function SubscribeBox({ token, subscribedEmail }) {
  const [email, setEmail] = useState('')
  const [state, setState] = useState('idle')   // idle | sending | done | error
  const [msg, setMsg] = useState('')

  async function submit(e) {
    e.preventDefault()
    if (state === 'sending') return
    const v = email.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { setState('error'); setMsg('Enter a valid email.'); return }
    setState('sending'); setMsg('')
    try { await subscribeToJourney(token, v); setState('done') }
    catch (err) { setState('error'); setMsg(err.message || 'Could not subscribe.') }
  }

  // Signed up already (Hub access signs people up, 2026-11-03): say where the emails go.
  if (state === 'done' || subscribedEmail) {
    return (
      <div data-tour="updates" style={S.subWrap}>
        <div style={S.subTitle}>You're getting email updates</div>
        <div style={S.subDone}>{state !== 'done' && subscribedEmail ? `We email ${subscribedEmail} when there's something new to see. Each email has a link to unsubscribe.` : "We'll email you when there's something new to see."}</div>
      </div>
    )
  }
  return (
    <form data-tour="updates" style={S.subWrap} onSubmit={submit}>
      <div style={S.subTitle}>Get email updates</div>
      <div style={S.subSub}>A note when something new is added — nothing more.</div>
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <input type="email" value={email} placeholder="you@email.com"
          onChange={ev => { setEmail(ev.target.value); if (state === 'error') setState('idle') }}
          style={S.subInput} />
        <button type="submit" disabled={state === 'sending'} style={S.subBtn}>
          {state === 'sending' ? '…' : 'Notify me'}
        </button>
      </div>
      {state === 'error' && <div style={S.subErr}>{msg}</div>}
    </form>
  )
}

const KEYFRAMES = `
.aj-jrn-lines { display: inline-flex; flex-direction: column; gap: 3px; }
.aj-jrn-lines i { display: block; width: 16px; height: 1.5px; background: rgba(255,255,255,0.9); border-radius: 1px; }
.aj-jrn-lines i:nth-child(2) { width: 11px; }
.aj-jrn-lines i:nth-child(3) { width: 14px; }
.aj-jrn-scroll::-webkit-scrollbar { width: 10px; }
.aj-jrn-scroll::-webkit-scrollbar-track { background: transparent; }
.aj-jrn-scroll::-webkit-scrollbar-thumb { background: rgba(7,39,45,0.18); border-radius: 8px; border: 2px solid transparent; background-clip: padding-box; }
.aj-jrn-scroll::-webkit-scrollbar-thumb:hover { background: rgba(7,39,45,0.32); border: 2px solid transparent; background-clip: padding-box; }
.aj-jrn-scroll { scrollbar-width: thin; scrollbar-color: rgba(7,39,45,0.25) transparent; }
@keyframes ajJrnMore { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(3px); } }
.aj-jrn-more-arrow { animation: ajJrnMore 2.4s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .aj-jrn-more-arrow { animation: none; } }
`

const S = {
  root: {
    position: 'fixed', inset: 0, zIndex: 1,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '0 20px', overflow: 'hidden',
  },
  header: {
    flexShrink: 0, textAlign: 'center', paddingTop: 36,
  },
  wordmark: {
    fontFamily: '"Montserrat", sans-serif', fontSize: 13, fontWeight: 500,
    letterSpacing: '0.34em', color: 'rgba(255,255,255,0.92)',
  },
  projectName: {
    fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 400,
    letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)',
    marginTop: 8,
  },
  stage: {
    position: 'relative', flex: 1, width: '100%', minHeight: 0, overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  // Fade anything approaching the left/right edges so neighbors read as sliding into / out of view.
  stageMask: {
    WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 92px, #000 calc(100% - 92px), transparent 100%)',
    maskImage: 'linear-gradient(to right, transparent 0, #000 92px, #000 calc(100% - 92px), transparent 100%)',
  },
  // Centered anchor; each slot positions itself relative to this line.
  track: { position: 'absolute', left: '50%', top: 0, bottom: 0, width: 0 },
  // The phase divider sits centered on its x in the gap between two cards (translateX moves it).
  phaseDivider: {
    position: 'absolute', top: '50%', left: -9, width: 18,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
    transition: 'transform 0.55s cubic-bezier(0.22,1,0.36,1), opacity 0.45s ease',
    pointerEvents: 'none',
  },
  phaseDividerLine: { flex: 1, width: 0, borderLeft: '0.5px solid rgba(255,255,255,0.38)' },
  phaseDividerLabel: {
    writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap',
    fontFamily: '"Montserrat", sans-serif', fontSize: 9, fontWeight: 500, letterSpacing: '0.2em',
    textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)',
  },
  slot: {
    position: 'absolute', top: 0, bottom: 0, left: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'transform 0.55s cubic-bezier(0.22,1,0.36,1), opacity 0.45s ease',
    willChange: 'transform, opacity',
  },
  // Outer card clips to the rounded corners (overflow:hidden); the inner wrapper scrolls, so the
  // scrollbar never breaks out of the radius. Padding lives on the inner scroller (S.cardScroll).
  // Near-square corners + a warm near-white read editorial (monograph plate) rather than app-like.
  card: {
    position: 'relative', width: '100%', overflow: 'hidden',
    background: 'rgba(253,253,251,0.96)', backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '0.5px solid rgba(255,255,255,0.55)', borderRadius: 3,
    boxShadow: '0 28px 80px rgba(4,21,24,0.42)',
  },
  cardScroll: { flex: 1, minHeight: 0, padding: 'clamp(34px, 5vw, 56px)' },
  // The "more below" cue (CardScroll): a fade from the card colour plus a quiet chevron.
  moreFade: {
    position: 'absolute', left: 0, right: 0, bottom: 0, height: 64,
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 6,
    background: 'linear-gradient(to bottom, rgba(253,253,251,0), rgba(253,253,251,0.96) 70%)',
    pointerEvents: 'none', transition: 'opacity 0.25s ease',
  },
  moreBtn: {
    width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'none', border: 'none', cursor: 'pointer', color: '#6f8f9b', padding: 0,
  },
  cardDate: {
    position: 'absolute', top: 20, right: 22,
    fontFamily: '"DM Mono", monospace', fontSize: 9.5, letterSpacing: '0.16em',
    textTransform: 'uppercase', color: '#aebfc7',
  },
  draftPill: {
    display: 'inline-block', marginBottom: 14,
    fontFamily: '"DM Mono", monospace', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.06em',
    textTransform: 'uppercase', color: '#b8540a', background: '#fef5eb',
    padding: '4px 10px', borderRadius: 20,
  },
  drawerDraft: {
    fontFamily: '"DM Mono", monospace', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.06em',
    textTransform: 'uppercase', color: '#f0b27a', border: '0.5px solid rgba(240,178,122,0.5)',
    padding: '2px 6px', borderRadius: 20, flexShrink: 0,
  },
  drawerNewBadge: {
    fontFamily: '"DM Mono", monospace', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', color: '#fff', background: '#1a6fa8',
    padding: '2px 7px', borderRadius: 20, flexShrink: 0,
  },
  newPill: {
    display: 'inline-block', marginBottom: 14,
    fontFamily: '"DM Mono", monospace', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.06em',
    textTransform: 'uppercase', color: '#fff', background: '#1a6fa8',
    padding: '4px 10px', borderRadius: 20,
  },
  footer: {
    flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 28, paddingBottom: 32, paddingTop: 8,
  },
  nav: {
    fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600,
    color: 'rgba(255,255,255,0.92)', background: 'transparent', border: 'none',
    cursor: 'pointer', padding: '8px 6px', transition: 'opacity 0.15s',
  },
  progress: {
    fontFamily: '"DM Mono", monospace', fontSize: 12, color: 'rgba(255,255,255,0.7)',
    letterSpacing: '0.08em', minWidth: 64, textAlign: 'center',
  },
  // Roadmap toggle (top-left, subtle)
  contentsBtn: {
    position: 'fixed', top: 34, left: 24, zIndex: 3,
    display: 'flex', alignItems: 'center', gap: 9,
    background: 'transparent', border: 'none', cursor: 'pointer',
    padding: 6, transition: 'opacity 0.2s',
  },
  contentsLabel: {
    fontFamily: '"Montserrat", sans-serif', fontSize: 9, fontWeight: 500,
    letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.9)',
  },
  // Combined notification badge — absolute, upper-left of the hamburger icon
  combinedDot: { position: 'absolute', top: 2, left: 1, width: 10, height: 10, borderRadius: '50%', background: '#F59E0B', border: '1.5px solid rgba(6,30,36,0.7)', boxShadow: '0 0 0 2px rgba(245,158,11,0.35)', zIndex: 1, pointerEvents: 'none' },
  // Amber dot reused in drawer shortcuts and agreements modal — same hue, inline
  agreementsDot: { width: 8, height: 8, borderRadius: '50%', background: '#F59E0B', boxShadow: '0 0 0 3px rgba(245,158,11,0.25)', flexShrink: 0 },
  // Agreement status pills — filled backgrounds mirror the invoice Paid/Unpaid style.
  agStatus: { fontFamily: '"DM Sans", sans-serif', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 20, flexShrink: 0 },
  agStatusSigned: { background: '#eaf4ee', color: '#1a7a4a' },
  agStatusPending: { background: '#fdf4e7', color: '#B7651A' },
  // Scrim
  scrim: {
    position: 'fixed', inset: 0, zIndex: 4, background: 'rgba(4,21,24,0.4)',
    transition: 'opacity 0.28s ease', backdropFilter: 'blur(1px)',
  },
  // Left drawer
  drawer: {
    position: 'fixed', top: 0, left: 0, bottom: 0, width: 300, maxWidth: '82vw', zIndex: 5,
    background: 'rgba(6,30,36,0.92)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
    borderRight: '0.5px solid rgba(255,255,255,0.12)', boxShadow: '8px 0 40px rgba(4,21,24,0.5)',
    transition: 'transform 0.32s cubic-bezier(0.22,1,0.36,1)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  drawerHead: { padding: '34px 24px 18px', borderBottom: '0.5px solid rgba(255,255,255,0.1)', flexShrink: 0 },
  drawerEyebrow: { fontFamily: '"Montserrat", sans-serif', fontSize: 9, fontWeight: 500, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)' },
  drawerProject: { fontFamily: '"Montserrat", sans-serif', fontSize: 14, fontWeight: 300, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.92)', marginTop: 8 },
  drawerSearch: {
    width: '100%', padding: '8px 26px 8px 10px', borderRadius: 6,
    border: '0.5px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)',
    color: '#fff', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, outline: 'none', boxSizing: 'border-box',
  },
  drawerSearchClear: {
    position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)',
    fontSize: 15, lineHeight: 1, padding: 2,
  },
  drawerEmpty: {
    padding: '16px 12px', fontFamily: '"DM Sans", sans-serif', fontSize: 12,
    color: 'rgba(255,255,255,0.45)', fontStyle: 'italic',
  },
  drawerList: { flex: 1, overflowY: 'auto', padding: '10px 12px' },
  phaseHdr: {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
    background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 6,
    padding: '8px 8px', transition: 'background 0.12s',
  },
  phaseTri: { fontSize: 9, color: 'rgba(255,255,255,0.5)', flexShrink: 0, transition: 'transform 0.15s', display: 'inline-block', width: 10 },
  phaseName: { flex: 1, minWidth: 0, fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  phaseCount: { fontFamily: '"DM Mono", monospace', fontSize: 10, color: 'rgba(255,255,255,0.35)', flexShrink: 0 },
  drawerItem: {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
    background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 8,
    padding: '11px 12px', transition: 'background 0.12s',
  },
  drawerItemActive: { background: 'rgba(255,255,255,0.12)' },
  drawerNum: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'rgba(255,255,255,0.4)', flexShrink: 0, width: 20 },
  drawerItemBody: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  drawerItemTitle: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.92)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  drawerItemType: { fontFamily: '"DM Sans", sans-serif', fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.02em' },
  drawerItemDate: { fontFamily: '"DM Mono", monospace', fontSize: 10, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.04em', marginTop: 3 },
  drawerDot: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: '#1D9E75', flexShrink: 0 },

  // Shortcuts section — pinned above the subscribe box
  drawerShortcuts: { flexShrink: 0, borderTop: '0.5px solid rgba(255,255,255,0.1)', padding: '6px 12px 4px' },
  drawerShortcutBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
    background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 8,
    padding: '10px 12px', transition: 'background 0.12s',
  },
  drawerShortcutLabel: { fontFamily: '"Montserrat", sans-serif', fontSize: 9, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)' },

  // Subscribe box (drawer footer)
  subWrap: { flexShrink: 0, padding: '16px 18px 20px', borderTop: '0.5px solid rgba(255,255,255,0.1)' },
  subTitle: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)' },
  subSub: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'rgba(255,255,255,0.4)', marginTop: 5, lineHeight: 1.45 },
  subDone: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 5, lineHeight: 1.45 },
  subInput: { flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: 6, border: '0.5px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, outline: 'none' },
  subBtn: { flexShrink: 0, padding: '8px 12px', borderRadius: 6, border: 'none', background: 'rgba(255,255,255,0.92)', color: '#07272D', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
  subErr: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: '#f0a392', marginTop: 6 },

  // Small ringed dot above the empty-state copy — a quiet compass/plan-mark accent instead of
  // dropping straight into text, so the card reads as composed rather than sparse.
  emptyMark: { width: 40, height: 40, borderRadius: '50%', border: '1px solid rgba(7,39,45,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 22, flexShrink: 0 },
  emptyMarkDot: { width: 6, height: 6, borderRadius: '50%', background: 'rgba(7,39,45,0.32)' },
  emptyEyebrow: { fontFamily: '"Montserrat", sans-serif', fontSize: 9, fontWeight: 500, letterSpacing: '0.34em', textTransform: 'uppercase', color: '#8ba6b1', marginBottom: 14, textAlign: 'center' },
  emptyTitle: { fontFamily: '"Montserrat", sans-serif', fontSize: 21, fontWeight: 300, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#07272D', textAlign: 'center' },
  emptyBody: { fontFamily: '"DM Sans", sans-serif', fontSize: 15, lineHeight: 1.7, color: '#3a5260', marginTop: 16 },
  emptyCta: { marginTop: 26, padding: '10px 22px', borderRadius: 7, border: 'none', background: '#07272D', color: '#fff', fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, letterSpacing: '0.01em', cursor: 'pointer' },

  // Centered Agreements modal + ASR popup styles
  agModalOverlay: {
    position: 'fixed', inset: 0, zIndex: 56, background: 'rgba(7,39,45,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  agModal: {
    background: '#fff', borderRadius: 12, padding: '22px 24px', width: 460, maxWidth: '94vw',
    maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(7,39,45,0.3)',
  },
  agModalClose: { background: 'none', border: 'none', fontSize: 20, color: '#9ab8bd', cursor: 'pointer', lineHeight: 1, padding: 2 },
  drawerEyebrow2: { fontFamily: '"Montserrat", sans-serif', fontSize: 9, fontWeight: 500, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#7a9aaa' },
  agModalItem: {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
    background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 8,
    padding: '10px 4px', borderTop: '0.5px solid rgba(7,39,45,0.08)', transition: 'background 0.12s',
    WebkitTapHighlightColor: 'transparent',
  },
  agModalTitle: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 500, color: '#07272D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  agModalSub: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#6b8f95', marginTop: 3 },
  agModalEmpty: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#9ab8bd', fontStyle: 'italic', padding: '8px 0' },
}
